import { SettingsIcon } from 'assets/icons/SettingsIcon';
import { SettingsForm } from 'components/Form/Settings/SettingsForm';
import {
  SettingsInformation,
  toFormValues,
  toRequestBody,
} from 'components/Form/Settings/settingsFormValues';
import { useDeviceCategory } from 'components/Layout/useDeviceCategory';
import { SidePanel } from 'components/SidePanel/SidePanel';
import type { JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setIsError } from 'redux/ApiResponseStatusGlobalDisplay/slice';
import { getLegalCorporationConfig } from 'redux/LegalCorporation/selectors';
import { isSuperAdminUser } from 'redux/User/selectors';
import { getClientCompanySingleLegalCorporationId } from 'services/api/common';
import { updateSettings } from 'services/api/legalCorporationConfig/client';
import { HttpError } from 'services/api/types';
import { PLATFORM_REVISED_LEGAL_CORPORATION_ID } from 'services/authentication/msalConfig';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';

type Props = {
  isOpen: boolean;
  onAbort: () => void;
  onSettingsUpdateSuccess: () => void;
};

export const SettingsSidePanel = ({
  isOpen,
  onAbort,
  onSettingsUpdateSuccess,
}: Props): JSX.Element => {
  const isSuperAdmin = useSelector(isSuperAdminUser);
  const legalCorporationConfig = useSelector(getLegalCorporationConfig);
  const SettingsIntl = useIntlByKey('settings-form');
  const initialFormValues = toFormValues(legalCorporationConfig);
  const dispatch = useDispatch();

  const onSubmit = async (settingsInfo: SettingsInformation) => {
    const legalCorporationId = getClientCompanySingleLegalCorporationId(
      PLATFORM_REVISED_LEGAL_CORPORATION_ID,
    );
    if (legalCorporationId && isSuperAdmin) {
      await updateSettings(toRequestBody(settingsInfo, legalCorporationId, initialFormValues));
      onSettingsUpdateSuccess();
    }
  };

  const onSubmitError = (_httpError: HttpError) => {
    dispatch(setIsError(true));
  };

  const { isMobile, isTablet } = useDeviceCategory();
  return (
    <>
      <SidePanel
        title={SettingsIntl('title')}
        titleIcon={<SettingsIcon />}
        isOpen={isOpen}
        onClose={onAbort}
        widthInPercent={isMobile ? 100 : isTablet ? 60 : undefined}
        isMobile={isMobile || isTablet}
      >
        <SettingsForm
          onSubmit={onSubmit}
          onSubmitError={onSubmitError}
          onAbort={onAbort}
          isReadOnly={!isSuperAdmin}
          initialFormValues={initialFormValues}
        />
      </SidePanel>
    </>
  );
};
